import React from 'react';
import { DollarSign, CheckCircle } from 'lucide-react';
import type { ValuationSummary as ValuationSummaryType } from '../../../types/acquisitionAnalysis';

interface ValuationSummaryProps {
  data: ValuationSummaryType;
}

export const ValuationSummary: React.FC<ValuationSummaryProps> = ({ data }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const multiplierData = [
    { label: '2x', value: data.multipliers.multiplier2x, color: 'slate' },
    { label: '3x', value: data.multipliers.multiplier3x, color: 'blue' },
    { label: '4x', value: data.multipliers.multiplier4x, color: 'indigo' },
    { label: '5x', value: data.multipliers.multiplier5x, color: 'purple' },
    { label: '6x', value: data.multipliers.multiplier6x, color: 'pink' },
  ];

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; text: string; border: string }> = {
      slate: {
        bg: 'bg-slate-500/10',
        text: 'text-slate-300',
        border: 'border-slate-500/30',
      },
      blue: {
        bg: 'bg-blue-500/10',
        text: 'text-blue-300',
        border: 'border-blue-500/30',
      },
      indigo: {
        bg: 'bg-indigo-500/10',
        text: 'text-indigo-300',
        border: 'border-indigo-500/30',
      },
      purple: {
        bg: 'bg-purple-500/10',
        text: 'text-purple-300',
        border: 'border-purple-500/30',
      },
      pink: {
        bg: 'bg-pink-500/10',
        text: 'text-pink-300',
        border: 'border-pink-500/30',
      },
    };
    return colors[color] || colors.slate;
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-2">Base Trail Income</h3>
        <p className="text-3xl font-bold text-indigo-400 mb-6">
          {formatCurrency(data.baseTrailIncome)}
        </p>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Valuation by Multiplier</h3>
        <div className="space-y-3">
          {multiplierData.map((item, index) => {
            const colors = getColorClasses(item.color);
            return (
              <div
                key={index}
                className={`p-4 rounded-lg border ${colors.bg} ${colors.border}`}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <DollarSign className={`w-5 h-5 ${colors.text}`} />
                    <span className={`text-sm font-semibold ${colors.text}`}>
                      {item.label} Multiple
                    </span>
                  </div>
                  <span className="text-xl font-bold text-white">
                    {formatCurrency(item.value)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-white mb-4">
          Factors Supporting Higher Multiplier (5-6x)
        </h3>
        <div className="space-y-2">
          {data.supportingFactors.map((factor, index) => (
            <div
              key={index}
              className="flex items-start gap-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg"
            >
              <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-300">{factor}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
        <p className="text-sm text-indigo-300">
          <strong>Valuation Note:</strong> The multiplier range reflects trail book quality,
          growth trajectory, broker retention, and risk factors. Higher multipliers (5-6x) are
          justified by strong growth, low attrition, and minimal clawback exposure.
        </p>
      </div>
    </div>
  );
};
